import React, { useMemo } from 'react';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import { useSecuredExternalLayers } from './hooks/useSecuredExternalLayers';
import type { ChatSelectedMapLayer } from './utils/mapLayersForAI';

type ChatLayerSelectorProps = {
  selectedLayers: ChatSelectedMapLayer[];
  onChange: (layers: ChatSelectedMapLayer[]) => void;
};

function layerKey(layer: Pick<ChatSelectedMapLayer, 'kind' | 'id'>) {
  return `${layer.kind}:${layer.id}`;
}

/** DOM-safe id (colons are awkward in htmlFor / getElementById). */
function layerDomId(layer: Pick<ChatSelectedMapLayer, 'kind' | 'id'>) {
  return `ai-layer-${layer.kind}-${layer.id}`;
}

/**
 * Multi-select of internal (PRATUS-hosted) and external (ArcGIS-hosted) layers
 * for the Map Layers chat data source. Selecting none keeps the legacy
 * behavior: the agent may consult any layer the workspace can see. This is an
 * advisory priority for the assistant, not a filter. Selections carry
 * authType/serviceUrl for display and credential lookup only — those fields
 * never leave the browser (see mapLayersForAI).
 */
const ChatLayerSelector: React.FC<ChatLayerSelectorProps> = ({
  selectedLayers,
  onChange,
}) => {
  const groupGuid = useGroupGuid();
  const { data: incident } = useIncident();
  const incidentId = incident?.id;

  const { data: publicExternal, isLoading: loadingPublic } =
    useListExternalLayersQuery({
      filter: {
        authType: 'apiKey',
        groupGuid: groupGuid ?? '',
      },
      perPage: 200,
    });

  const { data: orgExternal, isLoading: loadingOrg } =
    useSecuredExternalLayers();

  const { data: internalLayers, isLoading: loadingInternal } =
    useListInternalLayersQuery({
      filter: {
        groupGuid: groupGuid ?? '',
      },
      perPage: 200,
    });

  const catalog = useMemo(() => {
    // Mirror what the assistant's layer tools can see: this incident's layers
    // plus Command Center layers (no incident). The list API's incident filter
    // would drop the Command Center ones, so the narrowing happens here.
    const inWorkspace = (layer: { incidentId?: string | null }) =>
      !layer.incidentId || layer.incidentId === incidentId;
    const external = [
      ...(publicExternal?.data ?? []),
      ...(orgExternal?.data ?? []),
    ].filter(inWorkspace);
    const seen = new Set<string>();
    const externalItems: ChatSelectedMapLayer[] = [];
    for (const layer of external) {
      const id = String(layer.id);
      if (seen.has(id)) continue;
      seen.add(id);
      externalItems.push({
        kind: 'external',
        id,
        name: layer.name || 'Untitled external layer',
        authType: layer.authType,
        serviceUrl: layer.serviceUrl,
      });
    }

    const internalItems: ChatSelectedMapLayer[] = (internalLayers?.data ?? [])
      .filter(inWorkspace)
      .map((layer) => ({
        kind: 'internal',
        id: String(layer.id),
        name: layer.name || 'Untitled internal layer',
      }));

    return { externalItems, internalItems };
  }, [
    publicExternal?.data,
    orgExternal?.data,
    internalLayers?.data,
    incidentId,
  ]);

  const selectedKeys = useMemo(
    () => new Set(selectedLayers.map(layerKey)),
    [selectedLayers]
  );

  const toggle = (layer: ChatSelectedMapLayer) => {
    const key = layerKey(layer);
    if (selectedKeys.has(key)) {
      onChange(selectedLayers.filter((l) => layerKey(l) !== key));
      return;
    }
    onChange([...selectedLayers, layer]);
  };

  const isLoading = loadingPublic || loadingOrg || loadingInternal;

  if (isLoading) {
    return <p className="txt--sml text-muted mb-0">Loading layers…</p>;
  }

  if (
    catalog.externalItems.length === 0 &&
    catalog.internalItems.length === 0
  ) {
    return (
      <p className="txt--sml text-muted mb-0">
        No layers available. Add ArcGIS or PRATUS layers in the map&apos;s
        Layers tab.
      </p>
    );
  }

  const renderItem = (layer: ChatSelectedMapLayer, hint?: string) => (
    <li key={layerKey(layer)} className="form-check mb-1">
      <input
        className="form-check-input"
        type="checkbox"
        id={layerDomId(layer)}
        checked={selectedKeys.has(layerKey(layer))}
        onChange={() => toggle(layer)}
      />
      <label className="form-check-label txt--sml" htmlFor={layerDomId(layer)}>
        <span className="d-block">{layer.name}</span>
        {hint && (
          <span className="d-block txt--xs text-muted mt-1">{hint}</span>
        )}
      </label>
    </li>
  );

  return (
    <div className="d-flex flex-column gap-3">
      <p className="txt--sml text-muted mb-0">
        Pick specific layers for the AI to answer from, or leave all unchecked
        to let it use any layer on the map.
      </p>
      {catalog.internalItems.length > 0 && (
        <div>
          <div className="fw-medium txt--sml mb-2">
            PRATUS-hosted (internal)
          </div>
          <ul className="list-unstyled mb-0">
            {catalog.internalItems.map((layer) => renderItem(layer))}
          </ul>
        </div>
      )}
      {catalog.externalItems.length > 0 && (
        <div>
          <div className="fw-medium txt--sml mb-2">
            ArcGIS-hosted (external)
          </div>
          <ul className="list-unstyled mb-0">
            {catalog.externalItems.map((layer) =>
              renderItem(
                layer,
                layer.authType === 'userAuth'
                  ? 'Secured layer — requires your ArcGIS sign-in from the Layers tab.'
                  : undefined
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ChatLayerSelector;
